import { useMutation } from "@tanstack/react-query";
import { apiClient } from "~/lib/client";
import { handleError, handleResponse } from "~/utils/response";
import { getQueryClient } from "~/providers/query.provider";
import { toast } from "sonner";
import { invalidateSessionsQuery, sessionByIdQueryOptions } from "../queries/session";
import { useOnboardingSessionStore } from '../../store/complete-onboarding-session-store';
// import { sessionKeys } from '../queries/keys'; // Make sure to import sessionKeys

// Define types
export interface ChatRequestData {
    message: string;
    thread_id?: string;
    session_id?: string;
    agent?: string; // Add agent parameter
}

interface Message {
    content: string;
    additional_kwargs: Record<string, any>;
    response_metadata: Record<string, any>;
    type: 'human' | 'ai';
    name: string | null;
    id: string | null;
    example: boolean;
}

interface FollowUpQuestion extends Message {
    tool_calls: any[];
    invalid_tool_calls: any[];
    usage_metadata: any;
    question: string;
    options: string[];
}

export interface Disease {
    disease_name?: string;
    image_prompt?: string;
    overview?: string;
    symptoms?: string[];
    red_flags?: string[];
    diagnosis?: string[];
    causes?: string[];
    prevention?: string[];
    treatment_page?: {
      red_flag_questions?: string[];
      additional_question?: string | null;
      additional_offer?: string | null; // Added based on your data
      medications?: {
          category?: string;
          medication?: string;
          quantity?: string;
          instructions?: string[];
          is_primary?: boolean;
          is_secondary?: boolean;
          is_extra?: boolean;
      }[];
    };
    general_questions?: string[];
    disease_id?: string;
    affected_area?: string[];
    image_urls?: string[];
  }
export interface ChatResponseData {
    messages: Message[];
    triage_state: {
        follow_up_question: FollowUpQuestion;
    };
    diagnosis: [
        disease: Disease
    ]
    run_id: string;
    user_id: string;
    thread_id: string;
    end: boolean;
}

export interface ChatResponse {
    success: boolean;
    data?: ChatResponseData;
    error?: string | null;
}

// Define types for updating chat history (adjust as needed)
export interface UpdateChatHistoryRequestData {
    session_id: string;
    // Add other fields for the update request body here
    message: any; // Placeholder
}

export interface UpdateChatHistoryResponseData {
    // Define the structure of the successful response data here
    success: boolean;
    message?: string;
}

export interface UpdateChatHistoryResponse {
    success: boolean;
    data?: UpdateChatHistoryResponseData;
    error?: string | null;
}

// Add a new query key for chat history if not already present
export const chatHistoryKeys = {
    all: ['chatHistory'] as const,
    detail: (sessionId: string) => [...chatHistoryKeys.all, sessionId] as const,
};

// Rest of the code remains the same
export const chatKeys = {
    all: ['chat'] as const,
    messages: () => [...chatKeys.all, 'messages'] as const,
};

export const useChatMutation = () => {
    const queryClient = getQueryClient();

    return useMutation<ChatResponse, unknown, ChatRequestData>({
        mutationFn: async (variables: ChatRequestData) => {
            try {
                // Determine the agent endpoint based on the agent parameter
                const agent = variables.agent || "alfie-chat"; // Default to alfie-chat
                const endpoint = `/agents/${agent}/invoke`;
                
                // Create request body without the agent field
                const { agent: _, ...requestBody } = variables;
                
                const response = await apiClient(endpoint, {
                    method: "POST",
                    body: JSON.stringify(requestBody),
                });

                return await handleResponse<ChatResponseData>(response);
            } catch (error) {
                return handleError(error);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: chatKeys.messages() });
            invalidateSessionsQuery(queryClient)
        },
        onError: () => {
            toast.error("Failed to send message");
        },
    });
};

export const useUpdateChatHistoryMutation = () => {
    const queryClient = getQueryClient();

    return useMutation<UpdateChatHistoryResponse, unknown, UpdateChatHistoryRequestData>({
        mutationFn: async (variables: UpdateChatHistoryRequestData) => {
            try {
                const endpoint = `/sessions/${variables.session_id}/chat-history`;

                // Remove session_id from the request body
                const { session_id, ...requestBody } = variables;

                // Assuming a PUT request for updating, change to POST if needed
                const response = await apiClient(endpoint, {
                    method: "POST",
                    body: JSON.stringify(requestBody),
                });

                return await handleResponse<UpdateChatHistoryResponseData>(response);
            } catch (error) {
                return handleError(error);
            }
        },
        onSuccess: (data, variables) => {
            // Invalidate relevant queries after successful update
            console.log(data)
            queryClient.invalidateQueries({ queryKey: chatHistoryKeys.detail(variables.session_id) });
            // toast.success("Chat history updated successfully");
        },
        onError: () => {
            toast.error("Failed to update chat history");
        },
    });
};

export const useSelectSessionDiagnosisMutation = () => {
    const queryClient = getQueryClient();

    return useMutation<ChatResponse, unknown, { session_id: string; disease_id: string }>({
        mutationFn: async ({ session_id, disease_id }) => {
            try {
                const endpoint = `/sessions/${session_id}/select-diagnosis`;
                const response = await apiClient(endpoint, {
                    method: "PUT",
                    body: JSON.stringify({ disease_id }),
                });

                return await handleResponse<ChatResponseData>(response);
            } catch (error) {
                return handleError(error);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: chatKeys.messages() });
            invalidateSessionsQuery(queryClient);
            toast.success("Diagnosis selected successfully");
        },
        onError: () => {
            toast.error("Failed to select diagnosis");
        },
    });
};

export const useEndOnboardingMutation = () => {
    const queryClient = getQueryClient();

    return useMutation<ChatResponse, unknown, { session_id: string }>({
        mutationFn: async ({ session_id }) => {
            try {
                const endpoint = `/sessions/${session_id}/ended`;
                const response = await apiClient(endpoint, {
                    method: "PUT",
                });

                console.log("onboard end response", response)
                return await handleResponse<ChatResponseData>(response);
            } catch (error) {
                return handleError(error);
            }
        },
        onSuccess: async () => {
            queryClient.invalidateQueries({ queryKey: chatKeys.messages() });

            // Get the onboardingSessionId and store actions from the store state
            const { onboardingSessionId, setOnboardingSessionId, setOnboardingSession } = useOnboardingSessionStore.getState();
            console.log("onboardinngid", onboardingSessionId);

            // Fetch the session data directly using the onboardingSessionId
            if (onboardingSessionId) {
                 try {
                    // Use fetchQuery with the key and queryFn from sessionByIdQueryOptions
                    const sessionData = await queryClient.fetchQuery(
                        sessionByIdQueryOptions(onboardingSessionId)
                    );
                    console.log("Fetched session data:", sessionData);
                    // Update the store with the fetched session data
                    if (sessionData) {
                         setOnboardingSessionId(sessionData.session_id);
                         setOnboardingSession(sessionData.onboard_session);
                    }

                } catch (fetchError) {
                    console.error("Failed to fetch session data:", fetchError);
                    // Optionally handle the fetch error
                }
            }

            toast.success("Onboarding completed successfully");
        },
        onError: () => {
            toast.error("Failed to complete onboarding");
        },
    });
};


